import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CommonValidators, EdlCommonModalService, SessionService } from '@edl-development/edl-common';
import { UiOnParamsChanged } from '@uirouter/angular';
import { StateService, Transition } from '@uirouter/core';
import { ToastrService } from 'ngx-toastr';
import { finalize, firstValueFrom, interval, map, startWith, Subscription } from 'rxjs';
import { InitializeEditModePolicy } from 'src/app/components/observation-comments/enums/initialize-edit-mode-policy.enum';
import { ObservationCommentService } from 'src/app/components/observation-comments/services/observation-comment/observation-comment.service';
import { FileAttachment } from 'src/app/model/attachment/file-attachment.model';
import { ObservationTypes } from 'src/app/model/enum/observation-types.enum';
import { DomainFilterType } from 'src/app/model/enum/observations/domain-filter-type.enum';
import { NtpeObservationSection } from 'src/app/model/enum/observations/ntpe/ntpe-observation-section.enum';
import { NTPEObservationStatus } from 'src/app/model/enum/observations/ntpe/ntpe-observation-status.enum';
import { Subdomain } from 'src/app/model/enum/observations/subdomain.enum';
import { RubricResponse } from 'src/app/model/enum/rubric/rubric-response.enum';
import { DomainLookup } from 'src/app/model/interfaces/DomainLookup.interface';
import { DomainNavigationData } from 'src/app/model/interfaces/DomainNavigationData.interface';
import { SubdomainLookup } from 'src/app/model/interfaces/SubdomainLookup.interface';
import { NTPERubricSaveDTO } from 'src/app/model/observation/ntpe/ntpe-save-rubric-dto.model';
import { NtpeModel } from 'src/app/model/observation/ntpe/ntpe.model';
import { ObservationSummaryDTO } from 'src/app/model/observation/observation-summary-dto.model';
import { ObservationListService } from 'src/app/services/observation-list.service';
import { NTPERubricFormBuilderService } from 'src/app/services/Observation/ntpe/ntpe-rubric-form-builder.service';
import { NtpeService } from 'src/app/services/Observation/ntpe/ntpe.service';
import { ResourceData } from 'src/app/services/resource/resource.service';
import { ObservationLookUpTypes } from '../../helpers/observation-lookup-types/observation-lookup-types.helper';

export const GOALS_TAB = 1;
export const ACTION_PLAN_TAB = 2;
export const MIDYEAR_TAB = 3;
export const ENDYEAR_TAB = 4;
export const RUBRIC_TAB = 5;
export const REQUIRED_FOCUS_AREAS = 1;

@Component({
    selector: 'app-ntpe-details',
    templateUrl: './ntpe-details.component.html',
    styleUrls: ['./ntpe-details.component.scss'],
    providers: [ObservationCommentService]
})
export class NtpeDetailsComponent implements OnInit, UiOnParamsChanged {
    @Input() tabIndex: number = 1;
    @Input() observationDetails?: NtpeModel;
    @Input() observationSummary?: ObservationSummaryDTO;
    @Input() observationFiles: FileAttachment[] = [];
    @Input() readOnly: boolean = false;
    @Input() editEnabled: boolean = true;
    @Input() resourceFile: ResourceData = {};

    ntpeObservationSections = NtpeObservationSection;
    canSendFeedback: boolean = false;
    maintenanceNotification?: Subscription;
    rubricFormValueChange?: Subscription;
    actionPlanSaved: boolean = true;

    public showTargetUser: boolean = false;
    public filterType?: DomainFilterType = DomainFilterType.FOCUS_AREAS;
    public currentRubricComponent?: Subdomain;
    public domains: DomainLookup[] = [];
    public domainNavigationData: DomainNavigationData = {};

    generalCommentsFilterOptions: Array<any> = [];
    generalCommentsDomainFilterId: string = 'Observation';
    observationType: any = ObservationTypes.NTPE;
    generalCommentsEditModePolicy: any = InitializeEditModePolicy.CLOSE_ALL

    actionPlanForm!: UntypedFormGroup;
    rubricForm!: UntypedFormGroup;
    busy: boolean = false;
    focusAreas: string[] = [];

    isTargetUser: boolean = false;
    isEvaluatingUser: boolean = false;

    public navigationObservationInfo!: {
        isDashboard: boolean;
        navigationObservationId?: string,
        navigationObservationType?: number
    };

    constructor(
        private formBuilder: UntypedFormBuilder,
        private ntpeService: NtpeService,
        private edlCommonModalService: EdlCommonModalService,
        private ntperubricformbuilderservice: NTPERubricFormBuilderService,
        private observationCommentService: ObservationCommentService,
        private observationListService: ObservationListService,
        private stateService: StateService,
        private sessionService: SessionService,
        private toastr: ToastrService,
        private transition: Transition) {
    }

    ngOnInit(): void {
        const currentUser = this.sessionService.user;
        this.setParams(this.transition);
        this.domains = ObservationLookUpTypes.generateDomains(this.resourceFile);
        this.readOnly = this.observationDetails?.completed === true;
        this.canSendFeedback = this.observationDetails?.evaluatingUserId !== currentUser?.UserID && this.observationDetails?.targetUserId !== currentUser?.UserID;
        this.observationCommentService.initialize(this.observationSummary?.itemType, this.observationSummary?.observationId);
        this.navigationObservationInfo = this.getNavigationObservationInfo(this.observationDetails);

        this.isTargetUser = currentUser?.UserID === this.observationDetails?.targetUserId;
        this.isEvaluatingUser = currentUser?.UserID === this.observationDetails?.evaluatingUserId;
        this.readOnly = this.readOnly || this.observationDetails?.completed === true;

        this.generateRubricFocusAreas();
        this.setParams(this.transition);

        this.buildActionPlanForm();
        this.buildRubricForm();

        this.maintenanceNotification = interval(60000)
            .pipe(
                startWith(0),
                map(() => this.autosave())
            ).subscribe((alert) => { });
    }

    ngOnDestroy(): void {
        this.autosave();
        this.maintenanceNotification?.unsubscribe();
        this.rubricFormValueChange?.unsubscribe();
    }

    uiOnParamsChanged(newParams: { [paramName: string]: any; }, trans?: Transition): void {
        this.setParams(trans);
        this.updateRubricDomains(this.filterType, this.currentRubricComponent, this.showTargetUser);
        this.updateDomainNavigationData(this.rubricForm, this.showTargetUser);
    }

    navigateTab(tabIndex: number) {
        this.autosave();
        this.tabIndex = tabIndex;
        this.stateService.go('.', { tabIndex: tabIndex, currentRubricComponent: this.currentRubricComponent, filterType: this.filterType });
    }

    private setParams(transition?: Transition | undefined) {
        if (transition?.success) {
            const { tabIndex, filterType, currentRubricComponent, showTargetUser } = transition.params();
            this.tabIndex = tabIndex;
            this.filterType = filterType;
            this.currentRubricComponent = currentRubricComponent;
            this.showTargetUser = showTargetUser || this.isTargetUser;
        }
    }

    private updateDomainNavigationData(form: FormGroup, showTargetUser: boolean) {
        this.domainNavigationData = this.ntperubricformbuilderservice.generateDomainNavigationData(form, showTargetUser);
    }

    private updateRubricDomains(filterType?: DomainFilterType, currentSubdomain?: Subdomain, showTargetUser: boolean = true) {
        this.filterType = filterType;
        this.showTargetUser = showTargetUser ?? false;

        if (this.currentRubricComponent !== currentSubdomain) {
            this.currentRubricComponent = currentSubdomain;
        }
    }

    private autosave() {
        if (this.observationDetails?.status !== NTPEObservationStatus.Authenticated) {
            if (this.actionPlanForm.dirty && this.actionPlanForm.valid && !this.busy) {
                this.onSaveActionPlan({ value: this.actionPlanForm.value }, true);
            }
            if (this.rubricForm?.dirty && (this.observationDetails?.status ?? 0) >= NTPEObservationStatus.InProgress && !this.busy) {
                this.onSaveRubric(this.rubricForm, this.showTargetUser, true);
            }
        }
    }

    returnToDashboard() {
        const { userId } = this.transition.params();
        this.observationListService.openObservation(userId, this.observationSummary);
    }

    private buildActionPlanForm() {
        this.actionPlanForm = this.formBuilder.group({
            formDomains: [],
            goal: [this.observationDetails?.goal, [Validators.required, CommonValidators.emptyString()]],
        });
    }

    private buildRubricForm() {
        const currentUser = this.sessionService.user;
        this.rubricForm = this.ntperubricformbuilderservice.generateForm(this.domains, this.observationDetails, currentUser);
        this.updateDomainNavigationData(this.rubricForm, this.showTargetUser);
        this.rubricFormValueChange = this.rubricForm?.valueChanges.subscribe(() => {
            this.updateDomainNavigationData(this.rubricForm, this.showTargetUser);
        });
    }

    publishAllCommentsClicked() {
        return this.observationCommentService.publishAllDrafts(`Draft Comment Warning`);
    }

    //#region Event Handlers
    onActionPlanChanged = () => {
        this.actionPlanSaved = false;
    }

    onApproveActionPlan = () => {
        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        if (this.validateActionPlanForm() && !this.busy) {
                            let ntpeId = this.observationDetails?.ntpeObservationId;
                            this.ntpeService.approveActionPlan(ntpeId as string)
                                .subscribe({
                                    next: () => { },
                                    error: () => {
                                        this.toastr.error("There was an error processing your request.", "NTP");
                                    },
                                    complete: () => {
                                        this.toastr.success("NTP approved successfully.", "NTP");
                                        this.returnToDashboard();
                                    }
                                })
                        }
                    }
                }
                );
            });
    }

    onFeedback = () => {
        const ntpeObservationId = this.observationDetails?.ntpeObservationId;
        if (ntpeObservationId) {
            firstValueFrom(
                this.ntpeService.submitFeedback(ntpeObservationId)
            ).then(() => this.toastr.success("Feedback Submitted Successfully", "NTP Observation"));
        }
    }

    onFocusChange({ subdomain, focused }: { subdomain?: SubdomainLookup, focused?: boolean }) {
        if (focused != undefined && subdomain?.Key) {
            this.busy = true;
            var componentId = this.observationDetails?.components?.find(c => c.component === subdomain.Key)?.componentID;
            this.ntpeService.changeFocusArea(this.observationDetails?.ntpeObservationId as string, componentId as number, focused)
                .pipe(finalize(() => this.busy = false))
                .subscribe({
                    next: () => { },
                    error: () => {
                        this.toastr.error("There was an error processing your request.", "NTP");
                    },
                    complete: () => {
                        this.stateService.go('.', { currentRubricComponent: subdomain?.Value, filterType: this.filterType, tabIndex: this.tabIndex }, { reload: true });
                    }
                });
        }
    }

    onDomainNavigation({subdomain} : { subdomain?: SubdomainLookup }){
        this.stateService.go('.', { tabIndex: this.tabIndex, currentRubricComponent: subdomain?.Value, filterType: this.filterType });
    }

    onSaveActionPlan({ value }: { value: NtpeModel }, autosave: boolean = false) {
        let packedNtpeModel = this.generateNtpeSaveModel(value);

        if (!this.busy && this.observationDetails?.permissions?.actionPlan.canSave) {
            this.busy = true
            this.ntpeService.save(packedNtpeModel)
                .pipe(finalize(() => this.busy = false))
                .subscribe({
                    next: () => { },
                    error: () => {
                        if (!autosave) {
                            this.toastr.error("There was an error processing your request.", "NTP");
                        }
                    },
                    complete: () => {
                        if (!autosave) {
                            this.toastr.success("NTP saved successfully.", "NTP");
                        }
                        this.actionPlanSaved = true;
                    }
                })
        }
    }

    onSubmitActionPlan({ value }: { value: NtpeModel }) {
        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        this.observationCommentService.reloadComments().finally(() => {
                            if (this.validateActionPlanForm() && !this.busy) {
                                let ntpeId = this.observationDetails?.ntpeObservationId;
                                this.ntpeService.onSubmitActionPlan(ntpeId as string)
                                    .subscribe({
                                        next: () => { },
                                        error: () => {
                                            this.toastr.error("There was an error processing your request.", "NTP");
                                        },
                                        complete: () => {
                                            this.toastr.success("NTP submitted successfully.", "NTP");
                                            this.returnToDashboard();
                                        }
                                    })
                            }
                        }
                        );
                    }
                }
                );
            });
    }

    // Midyear Update
    onApproveMidYearUpdate = () => {
        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        if (this.validateActionPlanForm() && !this.busy) {
                            let ntpeId = this.observationDetails?.ntpeObservationId;
                            this.ntpeService.approveMidYearUpdate(ntpeId as string)
                                .subscribe({
                                    next: () => { },
                                    error: () => {
                                        this.toastr.error("There was an error processing your request.", "NTP");
                                    },
                                    complete: () => {
                                        this.toastr.success("NTP approved successfully.", "NTP");
                                        this.returnToDashboard();
                                    }
                                })
                        }
                    }
                }
                );
            });
    }

    onSubmitMidYearUpdate(rubricForm?: FormGroup) {
        const ntpeRubricSaveDTO = this.generateSaveRubric(rubricForm);

        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        this.observationCommentService.reloadComments().finally(() => {
                            // this.validateActionPlanForm().then((isValid) => {
                            if (this.validateActionPlanForm() && !this.busy) {
                                this.ntpeService.submitMidYearUpdate(ntpeRubricSaveDTO)
                                    .subscribe({
                                        next: () => { },
                                        error: () => {
                                            this.toastr.error("There was an error processing your request.", "NTP");
                                        },
                                        complete: () => {
                                            this.toastr.success("NTP Update submitted successfully.", "NTP");
                                            this.returnToDashboard();
                                        }
                                    })
                            }
                            // });
                        })
                    }
                });
            });
    }

    onApproveEndYearUpdate = () => {
        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        if (this.validateActionPlanForm() && !this.busy) {
                            let ntpeId = this.observationDetails?.ntpeObservationId;
                            this.ntpeService.approveEndYearUpdate(ntpeId as string)
                                .subscribe({
                                    next: () => { },
                                    error: () => {
                                        this.toastr.error("There was an error processing your request.", "NTP");
                                    },
                                    complete: () => {
                                        this.toastr.success("NTP approved successfully.", "NTP");
                                        this.returnToDashboard();
                                    }
                                })
                        }
                    }
                }
                );
            });
    }

    onSubmitEndYearUpdate(rubricForm?: FormGroup) {
        const ntpeRubricSaveDTO = this.generateSaveRubric(rubricForm);

        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        this.observationCommentService.reloadComments().finally(() => {
                            // this.validateRubricForm().then((isValid) => {
                            if (this.validateActionPlanForm() && !this.busy) {
                                this.ntpeService.submitEndYearUpdate(ntpeRubricSaveDTO)
                                    .subscribe({
                                        next: () => { },
                                        error: () => {
                                            this.toastr.error("There was an error processing your request.", "NTP");
                                        },
                                        complete: () => {
                                            this.toastr.success("NTP Update submitted successfully.", "NTP");
                                            this.returnToDashboard();
                                        }
                                    })
                            }
                            // });
                        })
                    }
                });
            });
    }

    onSaveRubric(submittedForm?: FormGroup, isTargetUser?: boolean, autosave: boolean = false) {
        const ntpeRubricSaveDTO = this.generateSaveRubric(submittedForm);

        if (this.observationDetails?.permissions?.rubric.canSave) {

            firstValueFrom(
                this.ntpeService.saveNTPERubric(ntpeRubricSaveDTO)
            ).then(() => {
                if (!autosave) {
                    this.toastr.success('Rubric saved successfully.', 'Success');
                    this.stateService.go('.', {}, { reload: true })
                }
            });
        }
    }

    onUpdateRubric(submittedForm: FormGroup) {
        const ntpeRubricSaveDTO = this.generateSaveRubric(submittedForm);

        if (!this.atLeastRequiredFocusAreas()) { return; }

        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.observationCommentService.publishAllDrafts(`Draft Comment Warning`).then((result) => {
                    if (result == true) {
                        if (!this.busy) {
                            this.ntpeService.submitUpdate(ntpeRubricSaveDTO)
                                .subscribe({
                                    next: () => { },
                                    error: () => {
                                        this.toastr.error("There was an error processing your request.", "NTP");
                                    },
                                    complete: () => {
                                        this.toastr.success("NTP Update submitted successfully.", "NTP");
                                        this.returnToDashboard();
                                    }
                                })
                        }
                    }
                }
                );
            });
    }

    onSubmitRubric(submittedForm: FormGroup) {
        const ntpeRubricSaveDTO = this.generateSaveRubric(submittedForm);

        this.edlCommonModalService.confirm('Confirmation', 'Are you sure you want to submit?')
            .then(() => {
                this.publishAllCommentsClicked().then((result) => {
                    if (result == true) {
                        this.observationCommentService.reloadComments().finally(() => {
                            this.validateRubricForm().then((isValid) => {
                                if (isValid && !this.busy) {
                                    this.ntpeService.submitRating(ntpeRubricSaveDTO)
                                        .subscribe({
                                            next: () => { },
                                            error: () => {
                                                this.toastr.error("There was an error processing your request.", "NTP");
                                            },
                                            complete: () => {
                                                this.toastr.success("NTP Rubric submitted successfully.", "NTP");
                                                this.returnToDashboard();
                                            }
                                        })
                                }
                            });
                        })
                    }
                });
            });
    }
    //#endregion

    //#region Form Validation
    private validateActionPlanForm(): boolean {
        if (this.actionPlanForm.valid
            && this.atLeastRequiredFocusAreas()
        ) {
            return true;
        }
        if (this.actionPlanForm.invalid) {
            this.toastr.error("Please fill out the required fields.", "Required fields missing")
            this.actionPlanForm.markAllAsTouched();
        }
        return false;
    }

    private async validateRubricForm(): Promise<boolean> {
        if (this.rubricForm.valid
            && this.atLeastRequiredFocusAreas()
            && this.areFocusAreasAreRated(this.generateSaveRubric(this.rubricForm))
            && await this.areAllRubricComponentsRated(this.generateSaveRubric(this.rubricForm))
        ) {
            return Promise.resolve(true);
        }
        if (this.rubricForm.invalid) {
            this.toastr.error("Please fill out the required fields.", "Required fields missing")
            this.rubricForm.markAllAsTouched();
        }
        return Promise.resolve(false);
    }

    private atLeastRequiredFocusAreas() {
        var rv = this.focusAreas.length >= REQUIRED_FOCUS_AREAS;
        if (!rv) {
            this.toastr.error("At least one focus area is required.", "NTP Focus Areas");
        }

        for (let focus of this.focusAreas) {
            var componentComments = this.observationCommentService.getCommentsByComponent(focus);
            if (componentComments.filter(c => c.userId === this.observationDetails?.targetUserId).length === 0) {
                rv = false;
                let domain = this.domains.find(d => d.Subdomains?.find(c => c.Key === focus));
                let subdomain = domain?.Subdomains?.find(c => c.Key === focus);
                this.toastr.error(`Focus Area: ${subdomain?.Label} needs a comment.`, "NTP Focus Areas");
            }
        }

        return rv;
    }

    private areFocusAreasAreRated(ntpeRubricSaveDTO: NTPERubricSaveDTO) {
        let rv = true;
        for (let focus of this.focusAreas) {
            let componentDomain = this.domains.find(d => d.Subdomains?.find(c => c.Key === focus));
            let componentRating = ntpeRubricSaveDTO.rubricResponses[componentDomain?.Value ?? -1][focus];
            if (componentRating === RubricResponse.UNSELECTED) {
                rv = false;
                let domain = this.domains.find(d => d.Subdomains?.find(c => c.Key === focus));
                let subdomain = domain?.Subdomains?.find(c => c.Key === focus);
                this.toastr.error(`Focus Area: ${subdomain?.Label} needs a rating.`, "NTP Focus Areas");
            }
        }
        return rv;
    }

    private areAllRubricComponentsRated(ntpeRubricSaveDTO: NTPERubricSaveDTO): Promise<boolean> {
        const hasUnselectedResponse = Object
            .values(ntpeRubricSaveDTO.rubricResponses)
            .flatMap(domain => Object.values(domain))
            .some(response => response === RubricResponse.UNSELECTED);

        if (hasUnselectedResponse) {
            return new Promise<boolean>((resolve) => {
                this.edlCommonModalService
                    .confirm('Rubric Incomplete', 'You have not completed all fields for this Rubric. Are you sure you want to submit?')
                    .then(() => { resolve(true); })
                    .catch(() => { resolve(false); });
            });
        } else {
            return Promise.resolve(true);
        }
    }
    //#endregion

    //#region Generators
    private generateNtpeSaveModel(value: NtpeModel) {
        let packedModel = {
            ...this.observationDetails,
            ...value
        } as NtpeModel;

        return packedModel;
    }

    private getNavigationObservationInfo(observationDetails?: NtpeModel) {
        return {
            isDashboard: true,
            navigationObservationId: observationDetails?.ntpeObservationId,
            navigationObservationType: ObservationTypes.NTPE.Type
        };
    }

    private generateRubricFocusAreas() {
        let focusComponentIds = this.observationDetails?.foci?.map(f => f.componentId) ?? [];
        let focusComponents = this.observationDetails?.components?.filter(c => focusComponentIds.includes(c.componentID));
        this.focusAreas = focusComponents?.map(focus => focus.component) ?? []
    }

    private generateSaveRubric(submittedForm?: FormGroup): NTPERubricSaveDTO {
        const formDomains = submittedForm?.value?.formDomains ?? {};

        const rubricResponses = Object.keys(formDomains).reduce((domains: any, domain: string) => {
            const formComponents = formDomains[domain];
            domains[domain] = Object.keys(formComponents).reduce((components: any, component: string) => {
                const formResponses = formComponents[component];
                components[component] = this.isTargetUser ? formResponses.teacherResponse : formResponses.principalResponse;
                return components;
            }, {})
            return domains;
        }, {})

        return {
            ntpeObservationID: this.observationDetails?.ntpeObservationId,
            submissionUserID: this.isTargetUser ? this.observationDetails?.targetUserId : this.observationDetails?.evaluatingUserId,
            rubricResponses
        } as NTPERubricSaveDTO;
    }
    //#endregion
}
